import type {
  CompletedSwingGesture,
  GestureResolutionState,
  ShotIntent,
  SwingPathSample,
} from './ShotIntent';

export enum SwingState {
  Idle = 'IDLE',
  Aiming = 'AIMING',
  Backswing = 'BACKSWING',
  Downswing = 'DOWNSWING',
  Impact = 'IMPACT',
  FollowThrough = 'FOLLOW_THROUGH',
  Complete = 'COMPLETE',
}

export interface SwingInputSnapshot {
  readonly state: SwingState;
  readonly backswingPercent: number;
  readonly path: readonly SwingPathSample[];
  readonly impactPoint: { readonly x: number; readonly y: number } | null;
  readonly downswingSpeed: number;
  readonly acceleration: number;
  readonly straightness: number;
}

const MIN_BACKSWING_PIXELS = 34;
const AIM_DRAG_THRESHOLD_PIXELS = 18;
const MIN_DOWNSWING_SPEED_PIXELS_PER_SECOND = 180;

export class SwingInput {
  private state = SwingState.Idle;
  private pointerId: number | null = null;
  private origin: SwingPathSample | null = null;
  private previous: SwingPathSample | null = null;
  private previousSpeed = 0;
  private previousSpeedTimeSeconds = 0;
  private samples: SwingPathSample[] = [];
  private maxBackswingPixels = 0;
  private downswingSpeed = 0;
  private acceleration = 0;
  private straightness = 1;
  private impactPoint: { x: number; y: number } | null = null;
  private transitionPoint: { x: number; y: number } | null = null;
  private completedIntent: ShotIntent | null = null;
  private stateEnteredAtSeconds = 0;
  private aimDeltaPixels = 0;
  private resolutionState: GestureResolutionState = 'idle';
  private impactSampleIndex = -1;
  private transitionSampleIndex = -1;

  constructor(private readonly element: HTMLElement) {}

  begin(event: PointerEvent): void {
    if (!this.isTouchPointer(event) || this.pointerId !== null) return;
    this.pointerId = event.pointerId;
    this.element.setPointerCapture?.(event.pointerId);
    const sample = this.sampleFrom(event);
    this.origin = sample;
    this.previous = sample;
    this.previousSpeedTimeSeconds = sample.timeSeconds;
    this.samples = [sample];
    this.maxBackswingPixels = 0;
    this.downswingSpeed = 0;
    this.acceleration = 0;
    this.straightness = 1;
    this.impactPoint = null;
    this.transitionPoint = null;
    this.completedIntent = null;
    this.aimDeltaPixels = 0;
    this.impactSampleIndex = -1;
    this.transitionSampleIndex = -1;
    this.resolutionState = 'collecting';
    this.transition(SwingState.Aiming, sample.timeSeconds);
  }

  move(event: PointerEvent): void {
    if (!this.shouldHandle(event) || !this.origin || !this.previous) return;
    const sample = this.sampleFrom(event);
    this.samples.push(sample);
    const dxFromOrigin = sample.x - this.origin.x;
    const dyFromOrigin = sample.y - this.origin.y;

    if (this.state === SwingState.Aiming) {
      this.aimDeltaPixels += sample.x - this.previous.x;
      if (dyFromOrigin > MIN_BACKSWING_PIXELS)
        this.transition(SwingState.Backswing, sample.timeSeconds);
      else if (Math.abs(dxFromOrigin) > AIM_DRAG_THRESHOLD_PIXELS)
        this.transition(SwingState.Aiming, sample.timeSeconds);
    }

    if (this.state === SwingState.Backswing || this.state === SwingState.Aiming) {
      this.maxBackswingPixels = Math.max(this.maxBackswingPixels, dyFromOrigin);
      if (this.maxBackswingPixels > MIN_BACKSWING_PIXELS && sample.y < this.previous.y) {
        this.transitionPoint = { x: sample.x, y: sample.y };
        this.transitionSampleIndex = this.samples.length - 1;
        this.transition(SwingState.Downswing, sample.timeSeconds);
      }
    }

    if (this.state === SwingState.Downswing) {
      this.updateDownswingMetrics(sample);
      if (sample.y <= this.origin.y) this.registerImpact(sample);
    }

    if (this.state === SwingState.FollowThrough) this.resolutionState = 'followingThrough';

    this.previous = sample;
  }

  end(event: PointerEvent): void {
    if (!this.shouldHandle(event)) return;
    const releaseSample = this.sampleFrom(event);
    const last = this.samples.at(-1);
    if (
      !last ||
      last.timeSeconds !== releaseSample.timeSeconds ||
      last.x !== releaseSample.x ||
      last.y !== releaseSample.y
    )
      this.samples.push(releaseSample);
    const releasedAt = event.timeStamp / 1000;
    // Release, never crossing impact, is the sole touch/pen completion action.
    if (this.impactPoint && this.downswingSpeed >= MIN_DOWNSWING_SPEED_PIXELS_PER_SECOND) {
      this.resolutionState = 'released';
      this.completedIntent = this.buildIntent(releasedAt);
      this.resolutionState = 'resolved';
    } else this.resolutionState = 'cancelled';
    this.transition(SwingState.Complete, releasedAt);
    this.releasePointer(event.pointerId);
  }

  update(elapsedSeconds: number): void {
    // A stale pointer can be cancelled, but must never auto-launch.
    if (this.pointerId !== null && elapsedSeconds - this.stateEnteredAtSeconds > 30) this.cancel();
  }

  consumeShotIntent(): ShotIntent | null {
    const intent = this.completedIntent;
    this.completedIntent = null;
    if (this.state === SwingState.Complete) this.resetGesture();
    return intent;
  }

  consumeAimDelta(): number {
    const delta = this.state === SwingState.Aiming ? this.aimDeltaPixels : 0;
    this.aimDeltaPixels = 0;
    return delta;
  }

  snapshot(): SwingInputSnapshot {
    return {
      state: this.state,
      backswingPercent: this.powerPercent(),
      path: this.samples,
      impactPoint: this.impactPoint,
      downswingSpeed: this.downswingSpeed,
      acceleration: this.acceleration,
      straightness: this.straightness,
    };
  }

  cancel(): void {
    this.completedIntent = null;
    this.resolutionState = 'cancelled';
    this.releasePointer(this.pointerId);
    this.resetGesture();
  }

  private registerImpact(sample: SwingPathSample): void {
    this.impactPoint = { x: sample.x, y: sample.y };
    this.impactSampleIndex = this.samples.length - 1;
    this.resolutionState = 'impactCrossed';
    this.transition(SwingState.Impact, sample.timeSeconds);
    if (
      this.maxBackswingPixels < MIN_BACKSWING_PIXELS ||
      this.downswingSpeed < MIN_DOWNSWING_SPEED_PIXELS_PER_SECOND
    ) {
      this.resolutionState = 'cancelled';
      return;
    }
    this.transition(SwingState.FollowThrough, sample.timeSeconds);
  }

  private updateDownswingMetrics(sample: SwingPathSample): void {
    if (!this.previous || !this.origin) return;
    const dt = Math.max(0.001, sample.timeSeconds - this.previous.timeSeconds);
    const upwardPixels = Math.max(0, this.previous.y - sample.y);
    const speed = upwardPixels / dt;
    const speedDt = Math.max(0.001, sample.timeSeconds - this.previousSpeedTimeSeconds);
    this.acceleration = (speed - this.previousSpeed) / speedDt;
    this.previousSpeed = speed;
    this.previousSpeedTimeSeconds = sample.timeSeconds;
    this.downswingSpeed = Math.max(this.downswingSpeed, speed);
    const downswingSamples = this.samples.filter(
      (point) => point.timeSeconds >= this.stateEnteredAtSeconds,
    );
    const maxHorizontal = downswingSamples.reduce(
      (max, point) => Math.max(max, Math.abs(point.x - this.origin!.x)),
      0,
    );
    this.straightness = 1 - Math.min(1, maxHorizontal / Math.max(1, this.maxBackswingPixels));
  }

  private powerPercent(): number {
    const fullBackswingPixels = Math.max(180, window.innerHeight * 0.46);
    return Math.round(
      Math.min(
        100,
        Math.max(
          0,
          ((this.maxBackswingPixels - MIN_BACKSWING_PIXELS) /
            (fullBackswingPixels - MIN_BACKSWING_PIXELS)) *
            100,
        ),
      ),
    );
  }

  private buildIntent(releasedAtSeconds = this.nowSeconds()): ShotIntent {
    const gesture = this.buildCompletedGesture(releasedAtSeconds);
    return {
      power: this.powerPercent(),
      tempo: this.downswingSpeed,
      path: {
        samples: [...this.samples],
        maxBackswingPixels: this.maxBackswingPixels,
        impactPoint: this.impactPoint,
        straightness: this.straightness,
        transitionPoint: this.transitionPoint,
        releasePoint: this.samples.at(-1) ?? null,
      },
      faceAngle: 0,
      launchCommand: true,
      gesture,
    };
  }

  resolutionStatus(): GestureResolutionState {
    return this.resolutionState;
  }

  private buildCompletedGesture(releasedAtSeconds: number): CompletedSwingGesture {
    const origin = this.origin!;
    const transitionIndex = Math.max(0, this.transitionSampleIndex);
    const impactIndex = Math.max(transitionIndex, this.impactSampleIndex);
    const release = this.samples.at(-1)!;
    const reference = Math.max(120, this.element.getBoundingClientRect().height * 0.46);
    const followThroughPixels = Math.max(0, origin.y - release.y);
    const duration = (a: SwingPathSample, b: SwingPathSample) =>
      Math.max(0, b.timeSeconds - a.timeSeconds);
    const releasePrevious = this.samples.at(-2) ?? release;
    const releaseDistance =
      Math.hypot(release.x - releasePrevious.x, release.y - releasePrevious.y) || 1;
    const normalizedBackswingLength = Math.min(1.5, this.maxBackswingPixels / reference);
    const normalizedFollowThroughLength = Math.min(1.5, followThroughPixels / reference);
    const downswingDuration = duration(this.samples[transitionIndex], this.samples[impactIndex]);
    const backswingDuration = duration(origin, this.samples[transitionIndex]);
    const completionQuality = Math.min(1, normalizedFollowThroughLength / 0.35);
    return Object.freeze({
      samples: Object.freeze([...this.samples]),
      startedAtSeconds: origin.timeSeconds,
      releasedAtSeconds,
      originPoint: { x: origin.x, y: origin.y },
      transitionPoint: { ...this.transitionPoint! },
      impactPoint: { ...this.impactPoint! },
      releasePoint: { x: release.x, y: release.y },
      backswingSamples: Object.freeze(this.samples.slice(0, transitionIndex + 1)),
      downswingSamples: Object.freeze(this.samples.slice(transitionIndex, impactIndex + 1)),
      followThroughSamples: Object.freeze(this.samples.slice(impactIndex)),
      maxBackswingPixels: this.maxBackswingPixels,
      followThroughPixels,
      backswingDurationSeconds: backswingDuration,
      transitionDurationSeconds: Math.min(0.15, downswingDuration * 0.2),
      downswingDurationSeconds: downswingDuration,
      followThroughDurationSeconds: Math.max(
        0,
        releasedAtSeconds - this.samples[impactIndex].timeSeconds,
      ),
      peakDownswingSpeed: this.downswingSpeed,
      acceleration: this.acceleration,
      decelerationAfterImpact: 0,
      straightness: this.straightness,
      lateralPathAtImpactPixels: this.impactPoint!.x - origin.x,
      releaseDirection: {
        x: (release.x - releasePrevious.x) / releaseDistance,
        y: (release.y - releasePrevious.y) / releaseDistance,
      },
      normalizedBackswingLength,
      normalizedFollowThroughLength,
      tempoRatio: downswingDuration > 0 ? backswingDuration / downswingDuration : 1,
      transitionQuality: Math.max(
        0,
        1 - Math.abs(backswingDuration / Math.max(0.01, downswingDuration) - 3) / 3,
      ),
      impactAlignment: Math.max(0, 1 - Math.abs(this.impactPoint!.x - origin.x) / reference),
      strikeConfidence: Math.max(0, Math.min(1, this.straightness * completionQuality)),
      completionQuality,
    });
  }

  private sampleFrom(event: PointerEvent): SwingPathSample {
    const rect = this.element.getBoundingClientRect();
    return {
      x: event.clientX,
      y: event.clientY,
      timeSeconds: event.timeStamp / 1000,
      normalizedX: rect.width > 0 ? (event.clientX - rect.left) / rect.width : 0,
      normalizedY: rect.height > 0 ? (event.clientY - rect.top) / rect.height : 0,
    };
  }

  private nowSeconds(): number {
    return performance.now() / 1000;
  }

  private shouldHandle(event: PointerEvent): boolean {
    return this.pointerId === event.pointerId && this.isTouchPointer(event);
  }

  private isTouchPointer(event: PointerEvent): boolean {
    return event.pointerType === 'touch' || event.pointerType === 'pen';
  }

  private transition(state: SwingState, timeSeconds: number): void {
    this.state = state;
    this.stateEnteredAtSeconds = timeSeconds;
  }

  private releasePointer(pointerId: number | null): void {
    if (pointerId !== null && this.element.hasPointerCapture?.(pointerId))
      this.element.releasePointerCapture(pointerId);
    this.pointerId = null;
  }

  private resetGesture(): void {
    this.state = SwingState.Idle;
    this.origin = null;
    this.previous = null;
    this.samples = [];
    this.maxBackswingPixels = 0;
    this.downswingSpeed = 0;
    this.acceleration = 0;
    this.straightness = 1;
    this.impactPoint = null;
    this.transitionPoint = null;
    this.aimDeltaPixels = 0;
    if (this.resolutionState !== 'cancelled') this.resolutionState = 'idle';
  }
}
